import React, { useCallback } from "react"
import { Container, Card, CardHeader, ListGroupItem, ListGroup } from "reactstrap"
import { Input, FormGroup } from "reactstrap"
import { Trans, useLingui } from "@lingui/react"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import { selectCreatedVslaInfo } from "../../store/reducers/getVslas"
import { postCreateGroup } from "../../store/actions/createVsla"

import Settings from "./Settings"
import FundSummary from "./FundSummary"
import MemberMenu from "../members/MemberMenu"
import GroupSummary from "./GroupSummary"
import VslaMenu from "./VslaMenu"
import { setCurrentPage } from "../../store/reducers/navigation"
import PayFine from "../members/pay-fine"
import MemberOverview from "../members/member-overview"
import RepayLoan from "../members/loans/repay"
import WriteOffLoan from "../members/WriteOffLoan"
import CreateLoan from "../members/loans/create"
import SellShares from "../members/sell-shares"
import BuyShares from "../members/buy-shares"
import WelfareGiven from "../members/welfare/contribute"
import MembershipFee from "../members/membership-fee"
import AgriContribution from "../members/agri-contribution"
import WelfareRequest from "../members/welfare/request"
import MemberAction from "../members/member-actions"
import AddMemberMenu from "../members/AddMemberMenu"
import AddExistingMembers from "../members/AddExistingMembers"
import AssignMemberRoles from "../members/AssignMemberRoles"
import MeetingsMenu from "../meetings/MeetingsMenu"
import PreviousMeetings from "../meetings/PreviousMeetings"
import CreateMeeting from "../meetings/CreateMeeting"
import PreviousMeetingItem from "../meetings/MeetingItem"
import ActiveMeetingTransactions from "../meetings/ActiveMeetingTransactions"
import AddMember from "../members/AddMember"
import AddGroup from "./AddGroup"
import AddGroupFund from "./AddGroupFund"
import { getUser, logoutAction } from "../../store/actions/auth"
import OfflineMeetingItem from "../meetings/OfflineMeetingItem"
import otherIncome from "../otherIncomeAndExpenses/otherIncome"
import otherExpenses from "../otherIncomeAndExpenses/otherExpenses"
import CashoutError from "../cashout/CashoutError"
import { canCashout } from "../../store/reducers/cashout/actions"
import EndCycleCashout from "../cashout/EndCycleCashout"
import { intToArabic } from "../../view/shared/utilities/arabicNumParser"
import { t } from "@lingui/macro"
import { arabicDateConverter } from "../../view/shared/utilities/arabicDateConverter"
import MidCycleCashout from "../cashout/MidCycleCashout"
import StellarLoader, { GroupCreateLoader } from "../layout/StellarLoader"
import CustomAlert from "../layout/CustomAlert"
import LearningContent from "../educationalContent"
import LessonView from "../educationalContent/lesson-view"
import LessonsView from "../educationalContent/lessons-view"

const loadingStyle = {
  fontFamily: "Fira Sans",
  fontSize: "20px",
}

const headerStyle = {
  color: "#E36F1E",
  fontFamily: "quicksand",
  fontWeight: "bolder",
  fontSize: "1.8em",
  textAlign: "center",
  paddingBottom: "0.5em",
}

const cardheaderStyle = {
  backgroundColor: "white",
  padding: ".5rem",
  textAlign: "center",
  fontWeight: "bold",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
}

const componentsMap = {
  "group-settings": Settings,
  "group-funds": FundSummary,
  "group-memberList": MemberMenu,
  "group-addMemberMenu": AddMemberMenu,
  "group-addExistingMembers": AddExistingMembers,
  "group-assignMemberRoles": AssignMemberRoles,
  "group-details": GroupSummary,
  "group-vslaMenu": VslaMenu,
  "group-payFine": PayFine,
  "group-overview": MemberOverview,
  "group-repayLoan": RepayLoan,
  "group-writeOffLoan": WriteOffLoan,
  "group-createLoan": CreateLoan,
  "group-sellShares": SellShares,
  "group-buyShares": BuyShares,
  "group-addWelfare": WelfareGiven,
  "group-addAgri": AgriContribution,
  "group-membershipFee": MembershipFee,
  "group-requestWelfare": WelfareRequest,
  "group-memberActions": MemberAction,
  "group-meetingsMenu": MeetingsMenu,
  "group-previousMeetings": PreviousMeetings,
  "group-createMeeting": CreateMeeting,
  "group-previousMeetingsItem": PreviousMeetingItem,
  "group-meetingTransactions": ActiveMeetingTransactions,
  "group-addMember": AddMember,
  "group-addFunds": AddGroupFund,
  "group-offlineMeeting": OfflineMeetingItem,
  "group-addGroupIncome": otherIncome,
  "group-addGroupExpense": otherExpenses,
  "group-cashOut": MidCycleCashout,
  "group-cashOutError": CashoutError,
  "group-endOfCycleCashout": EndCycleCashout,
  "group-createGroup": AddGroup,
  "group-learningPage": LearningContent,
  "group-lessonPage": LessonView,
  "group-lessonsPage": LessonsView,
}

const buttonStyle = {
  width: "100%",
  color: "#fff",
  background: "#e36f1e",
  border: "solid 1px #e36f1e",
  borderRadius: "3px",
  marginTop: "14px",
}

const SyncStatus = ({ group, editGroup }) => {
  const getStatus = useCallback(() => {
    return group?.syncStatus === "pending"
      ? "Pending Sync"
      : group?.syncStatus === "error"
        ? group?.error || "Error creating group"
        : ""
  }, [group])

  return (
    <small
      style={{
        textAlign: "center",
        color: group.syncStatus === "error" ? "red" : "#E36F1E",
        fontStyle: "italic",
        fontSize: "14px",
      }}
    >
      {getStatus()}
      {group.error && (
        <button onClick={editGroup} style={buttonStyle}>
          Edit
        </button>
      )}
    </small>
  )
}

SyncStatus.propTypes = {
  group: PropTypes.object,
  editGroup: PropTypes.func,
}

const Groups = (props) => {
  // eslint-disable-next-line no-unused-vars
  const { i18n } = useLingui()
  const [groupPayload, setPayload] = React.useState(null)
  const create_group = t({
    id: "title_copy.create_group",
    message: "Create Group",
  })

  const setPayloadData = (items) => setPayload({ ...groupPayload, ...items })
  React.useEffect(() => {
    if (!groupPayload || !Array.isArray(groupPayload.accounts)) {
      return
    }
    submitCreateGroup()
  }, [groupPayload])

  const submitCreateGroup = () => {
    if (groupPayload) {
      const data = groupPayload.group

      if (!data.establishment_date?.length) {
        delete data.establishment_date
      }
      props.sendGroupData({
        ...groupPayload,
        stellarToken: props.user.tokenBC,
      })
    }
  }

  const numberOfMembers = () => {
    const count = props.members.length
    return count
  }
  const DynamicComponent = componentsMap[props.currentPage]
  return (
    <>
      {props.isCreatingVsla &&
        !props.group.blockchain_key &&
        !props.offlineUploadError ? (
        <GroupCreateLoader
          msg={t({ id: "placeholder_copy.add_account_loader_msg" })}
        />
      ) : !props.isCreatingVsla && props.isLoading ? (
        <StellarLoader />
      ) : !props.isLoading &&
        !props.group &&
        !["group-addFunds", "group-createGroup"].includes(props.currentPage) ? (
        <div>
          <Container>
            <CustomAlert color="warning">
              <span style={{ fontSize: "16px", fontFamily: "Fira Sans" }}>
                <Trans id="title_copy.no_vsla" />
              </span>
            </CustomAlert>
            <FormGroup
              onClick={() => {
                props.setPage("group-createGroup")
              }}
            >
              <Input
                type="submit"
                value={create_group}
                name="button"
                className="btn btn-primary"
              />
            </FormGroup>
          </Container>
        </div>
      ) : [null, "group-main"].includes(props.currentPage) ? (
        <div>
          <div
            style={{
              backgroundColor: "white",
              padding: ".5rem",
              textAlign: "center",
            }}
          >
            <h4 style={headerStyle}>
              <Trans id="menu.group" />
            </h4>
          </div>
          <Container>
            <Card
              key={props.group?.id}
              name="group-card-view"
              className="shadow p-3 mb-5 bg-white rounded"
            >
              <span
                key={`${props.group?.id}-item`}
                onClick={(e) => {
                  if (props.group.error) {
                    e.preventDefault()
                    return
                  }
                  sessionStorage.setItem("current_group_id", props.group?.id)
                  props.setPage("group-vslaMenu")
                }}
                className="textStyle"
                style={{
                  ...loadingStyle,
                  textAlign:
                    i18n.locale === "ar" ? "right" : "left",
                }}
              >
                <CardHeader style={cardheaderStyle}>
                  <span>{props.group?.name}</span>
                  <SyncStatus
                    editGroup={() => props.setPage("group-createGroup")}
                    group={props.group}
                  />
                </CardHeader>
                <br />
                <p>
                  <Trans id="cards.total_member">Total Members</Trans>:{" "}
                  {i18n.locale === "ar"
                    ? intToArabic(numberOfMembers())
                    : numberOfMembers()}
                </p>
                <p>
                  <Trans id="cards.share_value" />:{" "}
                  {i18n.locale === "ar"
                    ? intToArabic(props.group?.cycle?.shareValue)
                    : props.group?.cycle?.shareValue}
                </p>
                {!["ar", "lb"].includes(i18n.locale) && (
                  <p>
                    <Trans id="cards.cycle_duration" />: {props.group.cycle_duration}{" "}
                    <Trans id="cards.months" />
                  </p>
                )}
                <hr />
                <p>
                  <Trans id="cards.country" />: {props.group?.country}
                </p>
                <p>
                  <Trans id="cards.locality" />: {props.group.locality}
                </p>
                <ListGroup>
                  {!["ar", "lb"].includes(i18n.locale) &&
                    props.group?.country !== "Lebanon" && (
                      <ListGroupItem>
                        <b>
                          <Trans id="cards.established_on" />
                        </b>
                        :{" "}
                        {props.group?.establishment_date ||
                          props.group.establishment_date}
                      </ListGroupItem>
                    )}
                  <ListGroupItem>
                    <b>
                      <Trans id="cards.created_at" />
                    </b>
                    :{" "}
                    {!["ar"].includes(i18n.locale) &&
                      new Date(props.group.created_at).toDateString()}
                    {["ar"].includes(i18n.locale) &&
                      arabicDateConverter(props.group.created_at)}
                  </ListGroupItem>
                </ListGroup>
              </span>
            </Card>
          </Container>
        </div>
      ) : (
        <>
          {!!DynamicComponent && (
            <DynamicComponent
              setPayloadData={setPayloadData}
              submitCreateGroup={submitCreateGroup}
            />
          )}
        </>
      )}
    </>
  )
}

Groups.propTypes = {
  sendGroupData: PropTypes.func,
  user: PropTypes.object,
  error: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  vslaSummary: PropTypes.array,
  currentPage: PropTypes.string,
  isLoading: PropTypes.bool,
  group: PropTypes.object,
  setPage: PropTypes.func,
  vsla: PropTypes.object,
}

const mapStateToProps = (state) => {
  return {
    isCreatingVsla: state.queue?.isUploading && state.queue?.category === "groups",
    offlineUploadError: state.queue?.failedToUpload,
    user: state.user,
    currentPage: state.currentPage,
    isLoading: state.isLoading,
    group: state.group,
    currentMember: state.loadedMember,
    vsla: state.vsla,
    error: state.vlsasLoadingError,
    vslaSummary: selectCreatedVslaInfo(state),
    members: state.loadedvslaMembers,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    setPage: (page) => dispatch(setCurrentPage(page)),
    getUser: () => dispatch(getUser()),
    canCashout: () => dispatch(canCashout()),
    logout: () => dispatch(logoutAction()),
    sendGroupData: (data) => dispatch(postCreateGroup(data)),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Groups)
